import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { ClientService, OrganizationService } from "@services";
import { Link } from "react-router";
import utils from "@utils";
import { Image } from "../../components";

import "./ClientRates.scss";

class ClientRates extends Component {
	constructor(props) {
		super(props);
		this.state = {
			clients: [],
			clientsLoading: false,
			changedInputs: [],
		};
	}

	componentDidMount = () => {
		const { user } = this.props;
		// re route if the user is not a coordinator
		if (!user.isCoordinator()) {
			window.location.href = "/";
		} else {
			this.getClients();
		}
	};

	/** Retrieves the list of clients available to the current user */
	getClients = () => {
		const { lang } = global;
		this.setState({ clientsLoading: true });

		ClientService.getAll({ getDetails: true, isRKPay: true })
			.then(({ data }) => {
				const clients = data.map(client => ({
					clientId: client.Id,
					organizationFee: client.OrganizationFee,
					value: client.OrganizationFee,
					photoUrl: client.ClientLogo,
					clientName: client.Name,
					isExpanded: false,
					hasError: false,
				}));
				this.setState({ clients });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading clients"),
				});
			})
			.finally(() => {
				this.setState({ clientsLoading: false });
			});
	};
	// Service Methods

	/**
	 * Handles expanding/collapsing a client as well as modifying a client's rate
	 * @param {Number} clientId The ID number of a client
	 * @param {Event} e The HTML event that triggered this method
	 */
	handleOnChangeClient = (clientId, e) => {
		let newValue;
		const { clients, changedInputs } = this.state;
		const {
			target: { value, type },
		} = e;
		const eventType = e.type;
		const newClients = clients.map(client => {
			if (client.clientId === clientId) {
				switch (eventType) {
					case "click":
					case "keypress":
						if (type !== "tel") {
							return { ...client, isExpanded: !client.isExpanded };
						}
						break;
					case "change":
						if (!value.match(/^\d*\.?\d{0,2}$/g)) {
							newValue = client.value;
						} else {
							newValue = value;
							changedInputs.push(`client-${clientId}`);
						}
						return { ...client, value: newValue, hasError: false };
					default:
						return client;
				}
			}
			return client;
		});

		this.setState({ clients: newClients, changedInputs });
	};

	/** Check that rates entered or changed for clients & locations are valid  */
	validateRates = () => {
		const { clients } = this.state;
		let isValid = true;

		const newClients = clients.map(client => {
			if (client.value === "") {
				isValid = false;
				return { ...client, hasError: true };
			}
			return client;
		});

		this.setState({ clients: newClients });
		return { isValid };
	};

	/**
	 * Saves the rate for a client or for a specific location (if `locationId` is provided)
	 * @param {Number} clientId The ID number for a client
	 */
	handleSaveIndividual = clientId => {
		const { lang } = global;
		const { clients } = this.state;
		const validation = this.validateRates();
		// when input values are invalid do not make the request
		if (!validation.isValid) return;

		const organizationFeeModelList = clients
			.filter(
				clientList =>
					clientList.value !== clientList.organizationFee &&
					clientList.clientId === clientId,
			)
			.map(clientList => ({
				ClientId: clientId,
				OrganizationFee: clientList.value,
			}));

		OrganizationService.updateOrganizationFees({ organizationFeeModelList })
			.then(() => {
				this.setState(prevState => ({
					clientsLoading: false,
					changedInputs: prevState.changedInputs.filter(
						item => item !== `client-${clientId}`,
					),
				}));
				utils.notify({
					type: "success",
					message: lang.t("Client rates successfully updated"),
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Unable to update client rates"),
				});
			});
	};

	/** Save rate changes for all clients and locations */
	handleSaveAll = () => {
		const { lang } = global;
		const { clients } = this.state;
		const validation = this.validateRates();
		// when input values are invalid do not make the request
		if (!validation.isValid) return;

		const organizationFeeModelList = clients
			.filter(clientList => clientList.value !== clientList.organizationFee)
			.map(clientList => ({
				ClientId: clientList.clientId,
				OrganizationFee: clientList.value,
			}));

		OrganizationService.updateOrganizationFees({ organizationFeeModelList })
			.then(() => {
				this.setState({
					clientsLoading: false,
					changedInputs: [],
				});
				utils.notify({
					type: "success",
					message: lang.t("Client rates successfully updated"),
					title: "Success",
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Unable to update client rates"),
				});
			});
	};

	render() {
		const { lang } = global;
		const { user } = this.props;
		const { clients, clientsLoading, changedInputs } = this.state;

		return (
			<Fragment>
				<div
					id="client-rates"
					className="container"
				>
					<Choose>
						<When condition={!user.IsRKPay}>
							<div className="client-rates-header row">
								<div className="col-xs-12 text-center">
									<h5>An RKPay account is required to view client rates</h5>
									<Link
										to="/my-account#RKPay"
										className="btn btn-primary"
									>
										Sign Up For RKPay
									</Link>
								</div>
							</div>
						</When>
						<Otherwise>
							<div className="client-rates-subheader row">
								<div className="col-xs-12">
									<h1>Change Hourly Rates</h1>
								</div>
								<div className="col-xs-12">
									<p>
										Please note that your new hourly rate will only apply to new
										contracts.
									</p>
									<p>
										Encourage your clients to activate RKPay for easy payments.
									</p>
								</div>
							</div>
							<div className="client-rates-list">
								<If condition={clientsLoading}>
									<div className="col-xs-12 text-center">
										<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
									</div>
								</If>
								<Choose>
									<When condition={clients.length === 0 && !clientsLoading}>
										<h3 className="text-muted text-center">
											You have not connected with any clients.
										</h3>
									</When>
									<Otherwise>
										<For
											each="client"
											of={clients}
										>
											<div
												className="row"
												key={`client_${client.clientId}`}
											>
												<div className="col-xs-8">
													<Image
														src={client.photoUrl}
														className="client-image img-circle pull-left"
														fallbackSrc="avatar"
													/>
													<strong
														title={client.clientName}
														className="client-name"
													>
														{client.clientName}
													</strong>
												</div>
												<div className="col-xs-4 col-sm-3">
													<i className="rkf rkf-dollar" />
													<input
														type="tel"
														name="clientRate"
														className={classNames(
															"form-control text-right",
															{
																"has-error": client.hasError,
															},
														)}
														value={client.value}
														onChange={e =>
															this.handleOnChangeClient(
																client.clientId,
																e,
															)
														}
														onKeyPress={e => {
															if (e.key === "Enter")
																this.handleSaveIndividual(
																	client.clientId,
																);
														}}
													/>
													/hr
												</div>
											</div>
										</For>
									</Otherwise>
								</Choose>
							</div>
						</Otherwise>
					</Choose>
				</div>
				<If condition={clients.length > 0 && changedInputs.length > 0}>
					<div
						id="client-rates-footer"
						className="container-fluid"
					>
						<div className="sticky-footer">
							<div className="col-xs-2 col-xs-offset-9 text-right">
								<button
									type="button"
									id="save-client-rate"
									className="btn btn-default"
									onClick={() => this.handleSaveAll()}
								>
									{`${lang.t("Save")} ${lang.t("All")}`}
								</button>
							</div>
						</div>
					</div>
				</If>
			</Fragment>
		);
	}
}

ClientRates.propTypes = {
	user: PropTypes.object,
};

ClientRates.defaultProps = {
	user: {},
};

export default ClientRates;
